import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

const SKIP_GLOBAL_ERROR_DIALOG_HEADER = 'X-Skip-Global-Error-Dialog';

export type ErpLeaveImportKind = 'erp_leave' | 'official_trip';

export interface ErpLeaveImportPublicationStatus {
  academic_term_id: number | null;
  import_kind: ErpLeaveImportKind;
  published: boolean;
  publish_reason: 'manual' | 'disbursement_date' | null;
  has_data: boolean;
  can_publish: boolean;
}

export interface ErpLeaveImportClearDataResponse {
  success: boolean;
  academic_term_id: number;
  import_kind: ErpLeaveImportKind;
  deleted_counts: {
    leave_import_rows: number;
    leave_records: number;
    publications: number;
    results: number;
    compensation_summary_snapshots: number;
  };
  rebuilt_counts: {
    results: number;
    result_items: number;
    compensation_summary_snapshots: number;
  };
  publication_status: ErpLeaveImportPublicationStatus;
}

export interface ErpLeaveImportApprovedRequest {
  id: number;
  schedule_submission_id: number;
  academic_term_id: number;
  leave_date: string;
  status: string;
  subject_code: string | null;
  subject_name: string | null;
  makeup_teaching_date: string | null;
  makeup_start_time: string | null;
  makeup_end_time: string | null;
  leave_type_name: string | null;
  leave_period_name: string | null;
  leave_period_code?: string | null;
  reason?: string;
  erp_record?: ErpLeaveImportRowResult | null;
  official_trip_record?: ErpLeaveImportRowResult | null;
  comparison_status?: ErpLeaveImportComparisonStatus;
  mismatch_reasons?: string[];
}

export interface ErpLeaveImportRowResult {
  row: number | null;
  leave_record_id?: number | null;
  academic_term_id: number;
  academic_term_name: string;
  academic_term_year: number;
  schedule_submission_id: number;
  leave_date: string;
  erp_person_number?: string | null;
  erp_person_name?: string | null;
  erp_full_name?: string | null;
  erp_department?: string | null;
  erp_leave_start_date?: string | null;
  erp_leave_end_date?: string | null;
  erp_submitted_at?: string | null;
  erp_last_updated_at?: string | null;
  erp_approval_status?: string | null;
  erp_adjustment_status?: string | null;
  leave_type?: string | null;
  is_active: boolean | null;
  reason?: string;
  approved_requests?: ErpLeaveImportApprovedRequest[];
  import_kind?: ErpLeaveImportKind;
  leave_period_code?: string | null;
  leave_period_name?: string | null;
  official_trip_record?: ErpLeaveImportRowResult | null;
  erp_record?: ErpLeaveImportRowResult | null;
  comparison_status?: ErpLeaveImportComparisonStatus;
  mismatch_reasons?: string[];
}

export type ErpLeaveImportComparisonStatus =
  | 'matched'
  | 'not_approved'
  | 'missing_erp'
  | 'missing_official_trip'
  | 'missing_both_external';

export interface ErpLeaveImportTeacherSummary {
  user_id: number;
  full_name: string;
  email: string;
  matched: ErpLeaveImportRowResult[];
  import_unmatched: ErpLeaveImportRowResult[];
  system_unmatched: ErpLeaveImportApprovedRequest[];
}

export interface ErpLeaveImportMatchSummary {
  teacher_count: number;
  matched_count: number;
  import_unmatched_count: number;
  system_unmatched_count: number;
  teachers: ErpLeaveImportTeacherSummary[];
}

export interface ErpLeaveImportResponse {
  success: boolean;
  message: string;
  saved_count: number;
  match_summary: ErpLeaveImportMatchSummary;
}

export interface ErpLeaveImportResultRow {
  id: number;
  sequence?: number;
  academic_term_id: number;
  academic_term_name: string | null;
  academic_term_year: number | null;
  user_id: number;
  full_name: string;
  email: string;
  matched_count: number;
  import_unmatched_count: number;
  system_unmatched_count: number;
  system_matched_request_count: number;
  system_unmatched_request_count: number;
  system_request_count: number;
  total_count: number;
  imported_at: string | null;
  imported_by_id: number | null;
  imported_by_name: string | null;
  matching_rule_version?: number;
}

export interface ErpLeaveImportResultDetail extends ErpLeaveImportResultRow {
  matched: ErpLeaveImportRowResult[];
  import_unmatched: ErpLeaveImportRowResult[];
  system_unmatched: ErpLeaveImportApprovedRequest[];
}

export interface ErpLeaveImportResultsResponse {
  data: ErpLeaveImportResultRow[];
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  total_page: number;
  offset?: number;
}

export interface ErpLeaveImportResultsParams {
  page?: number;
  limit?: number;
  keywords?: string;
  sort?: string | null;
  direction?: string | null;
}

export interface ErpLeaveImportLeaveRecordRow {
  id: number;
  sequence?: number;
  schedule_submission_id: number;
  academic_term_id: number;
  academic_term_name: string | null;
  academic_term_year: number | null;
  user_id: number;
  full_name: string;
  email: string;
  leave_date: string | null;
  erp_person_number: string | null;
  erp_person_name: string | null;
  erp_full_name: string | null;
  erp_department: string | null;
  erp_leave_start_date: string | null;
  erp_leave_end_date: string | null;
  erp_submitted_at: string | null;
  erp_last_updated_at: string | null;
  erp_approval_status: string | null;
  erp_adjustment_status: string | null;
  leave_type?: string | null;
  leave_period_code: string | null;
  leave_period_name: string | null;
  is_active: boolean | null;
  source: string;
  import_kind: ErpLeaveImportKind;
  imported_by_id: number | null;
  imported_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ErpLeaveImportCheckResultRow {
  id: string;
  sequence: number;
  import_row_id: number | null;
  row_number: number | null;
  import_kind: 'erp_leave' | 'official_trip';
  person_number: string | null;
  person_name: string | null;
  leave_type?: string | null;
  leave_start_date: string | null;
  leave_end_date: string | null;
  leave_period_code: string | null;
  leave_period_name: string | null;
  imported_at: string | null;
  raw_payload: Record<string, unknown> | null;
  system_request: ErpLeaveImportApprovedRequest | null;
  person_number_match: boolean;
  start_date_match: boolean;
  end_date_match: boolean;
  period_match: boolean | null;
  matched: boolean;
}

export interface ErpLeaveImportCheckSummaryRow {
  id: string;
  sequence?: number;
  person_number: string | null;
  user_id: number | null;
  full_name: string;
  email: string | null;
  user_found: boolean;
  system_matched_request_count: number;
  system_unmatched_request_count: number;
  system_request_count: number;
  imported_at: string | null;
}

export interface ErpLeaveImportCheckResultSummariesResponse {
  data: ErpLeaveImportCheckSummaryRow[];
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  total_page: number;
  offset?: number;
  erp_import_blocked?: boolean;
}

export interface ErpLeaveImportCheckResultsResponse {
  data: ErpLeaveImportCheckResultRow[];
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  total_page: number;
  offset?: number;
}

export interface ErpLeaveImportLeaveRecordsResponse {
  data: ErpLeaveImportLeaveRecordRow[];
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  total_page: number;
  offset?: number;
  erp_import_blocked?: boolean;
}

export interface ErpLeaveImportLeaveRecordsParams {
  page?: number;
  limit?: number;
  academic_term_id?: number | null;
  academic_term_name?: string | null;
  academic_term_year?: number | null;
  keywords?: string;
  sort?: string | null;
  direction?: string | null;
  person_number?: string | null;
  import_kind?: ErpLeaveImportKind;
}

@Injectable({
  providedIn: 'root',
})
export class ErpLeaveImportService {
  private apiUrl = '/erp_leave_import';

  #http = inject(HttpClient);

  exportTemplate(importKind?: ErpLeaveImportKind): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/export_template`, {
      params: this.compactParams({ import_kind: importKind }),
      responseType: 'blob',
    });
  }

  getResults(params: ErpLeaveImportResultsParams = {}): Observable<ErpLeaveImportResultsResponse> {
    return this.#http.get<ErpLeaveImportResultsResponse>(`${this.apiUrl}/results`, {
      params: this.compactParams(params),
    });
  }

  getResult(id: number): Observable<ErpLeaveImportResultDetail> {
    return this.#http.get<ErpLeaveImportResultDetail>(`${this.apiUrl}/results/${id}`);
  }

  getLeaveRecords(
    params: ErpLeaveImportLeaveRecordsParams = {},
  ): Observable<ErpLeaveImportLeaveRecordsResponse> {
    return this.#http.get<ErpLeaveImportLeaveRecordsResponse>(`${this.apiUrl}/leave_records`, {
      params: this.compactParams(params),
    });
  }

  getCheckResults(
    params: ErpLeaveImportLeaveRecordsParams = {},
  ): Observable<ErpLeaveImportCheckResultsResponse> {
    return this.#http.get<ErpLeaveImportCheckResultsResponse>(`${this.apiUrl}/check_results`, {
      params: this.compactParams(params),
    });
  }

  getCheckResultSummaries(
    params: ErpLeaveImportLeaveRecordsParams = {},
  ): Observable<ErpLeaveImportCheckResultSummariesResponse> {
    return this.#http.get<ErpLeaveImportCheckResultSummariesResponse>(`${this.apiUrl}/check_result_summaries`, {
      params: this.compactParams(params),
    });
  }

  getPublicationStatus(importKind?: ErpLeaveImportKind): Observable<ErpLeaveImportPublicationStatus> {
    return this.#http.get<ErpLeaveImportPublicationStatus>(`${this.apiUrl}/publication_status`, {
      params: this.compactParams({ import_kind: importKind }),
    });
  }

  publish(importKind?: ErpLeaveImportKind): Observable<ErpLeaveImportPublicationStatus> {
    return this.#http.post<ErpLeaveImportPublicationStatus>(`${this.apiUrl}/publish`, {
      ...(importKind == null ? {} : { import_kind: importKind }),
    });
  }

  clearData(
    academicTermId: number,
    importKind: ErpLeaveImportKind,
  ): Observable<ErpLeaveImportClearDataResponse> {
    return this.#http.delete<ErpLeaveImportClearDataResponse>(`${this.apiUrl}/clear_data`, {
      params: {
        academic_term_id: String(academicTermId),
        import_kind: importKind,
      },
      headers: {
        [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
      },
    });
  }

  importLeaveRecords(file: File, importKind?: ErpLeaveImportKind): Observable<ErpLeaveImportResponse> {
    const formData = new FormData();

    formData.append('file', file);
    if (importKind != null) {
      formData.append('import_kind', importKind);
    }

    return this.#http.post<ErpLeaveImportResponse>(`${this.apiUrl}/import`, formData, {
      headers: {
        [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true',
      },
    });
  }

  private compactParams(
    params: ErpLeaveImportResultsParams | ErpLeaveImportLeaveRecordsParams,
  ): Record<string, string> {
    return Object.entries(params).reduce<Record<string, string>>((result, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        result[key] = String(value);
      }

      return result;
    }, {});
  }
}
